import Close from '@mui/icons-material/Close';
import { Drawer, type DrawerProps } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import { Box } from '@ovComponents/1-primative/box/box';
import {
  Typography,
  type TypographyVariantOptions,
} from '@ovComponents/1-primative/typography/typography';
import { merge } from 'lodash-es';
import type { ElementType, ReactNode } from 'react';
import { IconButton } from '../iconButton/iconButton';
import { useSideDrawerTokens } from './sideDrawer.tokens';

type SideDrawerPassthroughProps = Omit<
  DrawerProps,
  'open' | 'anchor' | 'onClose' | 'children' | 'title'
>;

export type SideDrawerProps = SideDrawerPassthroughProps & {
  title: ReactNode;
  /** Root element for the title typography (e.g. `'h3'` for drawer headings). */
  titleComponent?: ElementType;
  /** Title typography variant (default matches legacy SideDrawer styling). */
  titleVariant?: TypographyVariantOptions;
  isOpen: boolean;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  /** Merged after default body layout tokens (e.g. `px: 0` for edge-to-edge children). */
  bodySx?: SxProps<Theme>;
  /** Merged after default footer tokens (e.g. `p: 0` when padding is applied per row inside `footer`). */
  footerSx?: SxProps<Theme>;
  hideBackdrop?: boolean;
  closeOnOutsideClick?: boolean;
  testIds?: { closeButton?: string };
};

export const SideDrawer = (props: SideDrawerProps): JSX.Element => {
  const {
    title,
    titleComponent = 'div',
    titleVariant = 'headingSmall',
    isOpen,
    onClose,
    children,
    footer,
    bodySx,
    footerSx,
    hideBackdrop = true,
    closeOnOutsideClick = false,
    testIds,
    sx,
    slotProps,
    ModalProps,
    BackdropProps,
    ...restDrawerProps
  } = props;

  const { sideDrawer: sideDrawerTokens } = useSideDrawerTokens();

  const effectiveHideBackdrop = closeOnOutsideClick
    ? false
    : (ModalProps?.hideBackdrop ?? hideBackdrop);

  return (
    <Drawer
      anchor='right'
      open={isOpen}
      onClose={() => {
        onClose();
      }}
      slotProps={slotProps}
      BackdropProps={{
        ...(closeOnOutsideClick ? { invisible: true } : {}),
        ...BackdropProps,
      }}
      ModalProps={{
        ...ModalProps,
        hideBackdrop: effectiveHideBackdrop,
      }}
      sx={merge({}, sideDrawerTokens.drawerRootSx, sx ?? {})}
      {...restDrawerProps}
    >
      <Box sx={sideDrawerTokens.headerSx}>
        <Typography component={titleComponent} variant={titleVariant}>
          {title}
        </Typography>
        <IconButton
          aria-label='Close'
          label='Close'
          onClick={onClose}
          testId={testIds?.closeButton}
          size='small'
        >
          <Close />
        </IconButton>
      </Box>
      <Box sx={merge({}, sideDrawerTokens.bodySx, bodySx ?? {})}>
        {children}
      </Box>
      {footer && (
        <Box sx={merge({}, sideDrawerTokens.footerSx, footerSx ?? {})}>
          {footer}
        </Box>
      )}
    </Drawer>
  );
};
